import { merge } from 'lodash'
import COUNTRIES from '@/data/countries'
import { PUBLIC_MEANS_OF_TRANSPORT } from '@/data/publicMeansOfTransport'
import { NO_LICENCE_SHARING, LICENCE_SHARING } from '@/data/sharingMeansOfTransport'
import routeSettingsDefaults from "@data/routeSettingsDefaults";

const ROUTE_QUERY_PARAMS_MAP = {
    avoid:  'vermeiden',
    travelMode:  'strecke',
    avoidTunnels: 'tunnelvermeiden',
    avoidCountries: 'laendervermeiden',
    publicTransportTypes: 'oeffentlicheverkehrsmittel',
    avoidBarriers: 'barrierenvermeiden',
    maxChanges:'maxumstiege',
    bikeCarriage: 'fahrradmitnahme',
    avoidInclines:  'anstiegevermeiden',
    preferCyclingInfrastructure: 'radinfrastruktur',
    sharingTransports: 'sharinganbieter',
}

export const ROUTE_OPTION_QUERY_PARAMS = Object.values(ROUTE_QUERY_PARAMS_MAP)

const ALLOWED_AVOID_OPTIONS = {
    'autobahn': 'motorway',
    'vignette': 'vignette',
    'maut': 'toll',
    'faehre': 'ferry',
}
const ALLOWED_AVOID_TUNNEL_OPTIONS = ['0', '500', '1000', '5000']

const ALLOWED_BARRIER_OPTIONS = {
    'treppen': 'STAIRS',
    'aufzuege': 'ELEVATOR',
    'rolltreppen': 'ESCALATOR',
}

const BASE_QUERY_OPTIONS = Object.fromEntries(Object.values(ROUTE_QUERY_PARAMS_MAP).map(entry => [entry, '']))

export function getRouteBaseQueryOptions() {
    return JSON.parse(JSON.stringify(BASE_QUERY_OPTIONS))
}

export function encodeRouteOptions(options) {
    let query = getRouteBaseQueryOptions()
    // motorized
    if(options.avoid) {
        let avoidQuery = []
        for(const key in ALLOWED_AVOID_OPTIONS) {
            const field = ALLOWED_AVOID_OPTIONS[key]
            if(options.avoid[field]) avoidQuery.push(key)
        }
        if(avoidQuery.length > 0) query[ROUTE_QUERY_PARAMS_MAP.avoid] = avoidQuery.join(',')
    }
    if(options.travelMode && options.travelMode === 'SHORTEST') {
        query[ROUTE_QUERY_PARAMS_MAP.travelMode] = 'kurz'
    }
    if(options.avoidTunnels && options.avoidTunnels !== '') {
        query[ROUTE_QUERY_PARAMS_MAP.avoidTunnels] = options.avoidTunnels
    }
    if(options.avoidCountries && options.avoidCountries.length > 0) {
        query[ROUTE_QUERY_PARAMS_MAP.avoidCountries] = options.avoidCountries.map(country => country.toLowerCase()).join(',')
    }

    // public
    if(options.meansOfTransport) {
        query[ROUTE_QUERY_PARAMS_MAP.publicTransportTypes] = options.meansOfTransport.join(',')
    }
    if(options.avoidBarriers) {
        let avoidBarriers = []
        for(const key in ALLOWED_BARRIER_OPTIONS) {
            const field = ALLOWED_BARRIER_OPTIONS[key]
            if(options.avoidBarriers.includes(field)) avoidBarriers.push(key)
            if(avoidBarriers.length > 0) query[ROUTE_QUERY_PARAMS_MAP.avoidBarriers] = avoidBarriers.join(',')
        }
    }
    if(options.maxChanges && options.maxChanges >= 0) {
        query[ROUTE_QUERY_PARAMS_MAP.maxChanges] = options.maxChanges
    }
    if(options.other && options.other.bikeCarriage) {
        query[ROUTE_QUERY_PARAMS_MAP.bikeCarriage] = 'ja'
    }

    // bike
    if(options.bikeParameters) {
        if(options.bikeParameters.avoidInclines) query[ROUTE_QUERY_PARAMS_MAP.avoidInclines] = 'ja'
        if(!options.bikeParameters.preferCyclingInfrastructure) query[ROUTE_QUERY_PARAMS_MAP.preferCyclingInfrastructure] = 'nein'
    }

    // sharing
    if(options.licence) {
        query[ROUTE_QUERY_PARAMS_MAP.sharingTransports] += options.licence.join(',')
    }
    if(options.noLicence) {
        query[ROUTE_QUERY_PARAMS_MAP.sharingTransports] += options.noLicence.join(',')
    }
    
    return query
}

export function decodeRouteOptions(query, transportMode) {
    const defaultRouteOptions = JSON.parse(JSON.stringify(routeSettingsDefaults))
    let queryRouteOptions = {}

    // motorized
    if(query[ROUTE_QUERY_PARAMS_MAP.avoid]) {
        const avoid = {}
        const avoidOptions = query[ROUTE_QUERY_PARAMS_MAP.avoid].split(',')
        for(const key in ALLOWED_AVOID_OPTIONS) {
            const field = ALLOWED_AVOID_OPTIONS[key]
            avoid[field] = avoidOptions.includes(key)
        }
        queryRouteOptions.avoid = avoid
    }
    if(query[ROUTE_QUERY_PARAMS_MAP.travelMode]) {
        queryRouteOptions.travelMode = query[ROUTE_QUERY_PARAMS_MAP.travelMode] === 'kurz' ? 'SHORTEST' : 'FASTEST'  
    }
    if(query[ROUTE_QUERY_PARAMS_MAP.avoidTunnels]) {
        if(ALLOWED_AVOID_TUNNEL_OPTIONS.includes( query[ROUTE_QUERY_PARAMS_MAP.avoidTunnels])) {
            queryRouteOptions.avoidTunnels = query[ROUTE_QUERY_PARAMS_MAP.avoidTunnels]
        }
    }
    if(query[ROUTE_QUERY_PARAMS_MAP.avoidCountries]) {
        const avoidCountries = []
        const avoidCountriesOptions = query[ROUTE_QUERY_PARAMS_MAP.avoidCountries].split(',')
        avoidCountriesOptions.forEach(countryCode => {
            if(COUNTRIES.find(country => country.value === countryCode || country.value === countryCode.toUpperCase())) {
                avoidCountries.push(countryCode.toUpperCase())
            }
        })
        if(avoidCountries.length > 0) queryRouteOptions.avoidCountries = avoidCountries
    }

    // public transport
    if(query[ROUTE_QUERY_PARAMS_MAP.publicTransportTypes]) {
        const meansOfTransport = []
        const publicTransportOptions = query[ROUTE_QUERY_PARAMS_MAP.publicTransportTypes].split(',')
        publicTransportOptions.forEach(option => {
            const matchingItem = PUBLIC_MEANS_OF_TRANSPORT.find(item => (item.label === option || item.value === option || item.urlSlug === option))
            if(matchingItem) {
                meansOfTransport.push(matchingItem.value)
            }
        })
        if(meansOfTransport.length > 0) queryRouteOptions.meansOfTransport = meansOfTransport
    }
    if(query[ROUTE_QUERY_PARAMS_MAP.avoidBarriers]) {
        const avoidBarriers = []
        const avoidBarriersOptions = query[ROUTE_QUERY_PARAMS_MAP.avoidBarriers].split(',')
        avoidBarriersOptions.forEach(option => {
            if(ALLOWED_BARRIER_OPTIONS[option]) {
                avoidBarriers.push(ALLOWED_BARRIER_OPTIONS[option])
            }
        })
        if(avoidBarriers.length > 0) queryRouteOptions.avoidBarriers = avoidBarriers
    }
    if(query[ROUTE_QUERY_PARAMS_MAP.maxChanges]) {
        const ALLOWED_MAX_CHANGES_OPTIONS = ['0', '1', '2', '3', '-1'];
        if(ALLOWED_MAX_CHANGES_OPTIONS.includes( query[ROUTE_QUERY_PARAMS_MAP.maxChanges])) {
            queryRouteOptions.maxChanges = query[ROUTE_QUERY_PARAMS_MAP.maxChanges]
        }
    }
    if(query[ROUTE_QUERY_PARAMS_MAP.bikeCarriage] && query[ROUTE_QUERY_PARAMS_MAP.bikeCarriage] === 'ja') {
        if(!queryRouteOptions.other) queryRouteOptions.other = {}
        queryRouteOptions.other.bikeCarriage = true
    }

    // bike
    if(query[ROUTE_QUERY_PARAMS_MAP.avoidInclines] && query[ROUTE_QUERY_PARAMS_MAP.avoidInclines] === 'ja') {
        if(!queryRouteOptions.bikeParameters) queryRouteOptions.bikeParameters = {}
        queryRouteOptions.bikeParameters.avoidInclines = true
    }
    if(query[ROUTE_QUERY_PARAMS_MAP.preferCyclingInfrastructure]) {
        if(!queryRouteOptions.bikeParameters) queryRouteOptions.bikeParameters = {}
        queryRouteOptions.bikeParameters.preferCyclingInfrastructure = query[ROUTE_QUERY_PARAMS_MAP.preferCyclingInfrastructure] === 'ja'
    }

    // sharing
    if(query[ROUTE_QUERY_PARAMS_MAP.sharingTransports]) {
        const noLicence = []
        const licence = []
        const sharingTransports = query[ROUTE_QUERY_PARAMS_MAP.sharingTransports].split(',')
        sharingTransports.forEach(transport => {
            const matchingNoLicenceItem = NO_LICENCE_SHARING.find(item => (item.value === transport || item.label === transport))
            if(matchingNoLicenceItem) noLicence.push(matchingNoLicenceItem.value)
            const matchingLicenceItem = LICENCE_SHARING.find(item => (item.value === transport || item.label === transport))
            if(matchingLicenceItem) licence.push(matchingLicenceItem.value)
        })
        queryRouteOptions.noLicence = noLicence
        queryRouteOptions.licence = licence
    }

    if(Object.keys(queryRouteOptions).length > 0) {
        return merge(defaultRouteOptions[transportMode], queryRouteOptions)
    }
    return false
}